import { type UserRole } from "@nexys/user-management-client";
import { AlertCircle, UserPlus } from "lucide-react";
import { useState, type FormEvent, type ReactNode } from "react";

import { useUmClient } from "../../context/auth-context.js";
import { errorMessage } from "../../lib/error-message.js";
import { UserRoleSelect } from "../roles/role-select.js";
import { Alert, AlertDescription } from "../ui/alert.js";
import { Button } from "../ui/button.js";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog.js";
import { Field } from "../ui/field.js";
import { Input } from "../ui/input.js";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select.js";

const SOCIAL_PROVIDERS = ["google", "microsoft", "github", "discord", "facebook"];

const providerLabel: Record<string, string> = {
  google: "Google",
  microsoft: "Microsoft",
  github: "GitHub",
  discord: "Discord",
  facebook: "Facebook",
};

/** Providers with a public handle an admin is likely to know. */
const handleLabel: Record<string, string> = {
  github: "GitHub username",
  discord: "Discord username",
};

export interface UserCreateDialogProps {
  /** Override the trigger button. */
  trigger?: ReactNode;
  onCreated?: (user: { id: string; email: string }) => void;
}

/** Superadmin-only direct user creation. */
export const UserCreateDialog = ({ trigger, onCreated }: UserCreateDialogProps) => {
  const client = useUmClient();
  const [open, setOpen] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  // "password" or a social provider id — mirrors the server's
  // password-XOR-provider contract on create-user.
  const [signInMethod, setSignInMethod] = useState("password");
  const [password, setPassword] = useState("");
  // Optional pre-link key for a social sign-in method; the server
  // falls back to the email when this is blank.
  const [providerAccountId, setProviderAccountId] = useState("");
  const [role, setRole] = useState<UserRole>("user");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const usesPassword = signInMethod === "password";

  const reset = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setSignInMethod("password");
    setPassword("");
    setProviderAccountId("");
    setRole("user");
    setError(null);
    setLoading(false);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const res = await client.adminCreateUser({
      firstName: firstName.trim(),
      lastName: lastName.trim() || undefined,
      email: email.trim(),
      ...(usesPassword
        ? { password }
        : {
            provider: signInMethod,
            ...(providerAccountId.trim() ? { providerAccountId: providerAccountId.trim() } : {}),
          }),
      role,
    });
    setLoading(false);
    if (res.ok) {
      onCreated?.(res.data.user);
      reset();
      setOpen(false);
    } else {
      setError(errorMessage(res.error, "Couldn't create user."));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <DialogTrigger asChild>
        {trigger ?? (
          <Button>
            <UserPlus />
            New user
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create a new user</DialogTitle>
          <DialogDescription>
            {usesPassword
              ? "The account is created active and email-verified. Share the " +
                "password securely — the user can change it from their profile."
              : "The account is created active and email-verified, with no " +
                "password — the user signs in with " +
                `${providerLabel[signInMethod]}.`}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4" noValidate>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="First name" htmlFor="new-user-first-name" required>
              <Input
                id="new-user-first-name"
                autoFocus
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Ada"
              />
            </Field>
            <Field label="Last name" htmlFor="new-user-last-name">
              <Input
                id="new-user-last-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Lovelace"
              />
            </Field>
          </div>
          <Field label="Email" htmlFor="new-user-email" required>
            <Input
              id="new-user-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ada@example.com"
            />
          </Field>
          <Field
            label="Sign-in method"
            htmlFor="new-user-sign-in-method"
            description={
              usesPassword
                ? undefined
                : `No password — their first ${providerLabel[signInMethod]} ` +
                  "sign-in lands on this account."
            }
          >
            <Select value={signInMethod} onValueChange={setSignInMethod}>
              <SelectTrigger id="new-user-sign-in-method">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="password">Password</SelectItem>
                {SOCIAL_PROVIDERS.map((p) => (
                  <SelectItem key={p} value={p}>
                    {providerLabel[p]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          {!usesPassword ? (
            <Field
              label={handleLabel[signInMethod] ?? `${providerLabel[signInMethod]} account id`}
              htmlFor="new-user-provider-account"
              description={
                handleLabel[signInMethod]
                  ? "Their handle on " +
                    `${providerLabel[signInMethod]}. Leave blank to match on the email ` +
                    "above instead — that only works if the provider reports it as one " +
                    "of their verified addresses."
                  : "Optional. Leave blank to match on the email above, which the " +
                    "provider must report as verified."
              }
            >
              <Input
                id="new-user-provider-account"
                value={providerAccountId}
                onChange={(e) => setProviderAccountId(e.target.value)}
                placeholder={handleLabel[signInMethod] ? "octocat" : ""}
                autoComplete="off"
              />
            </Field>
          ) : null}
          {usesPassword ? (
            <Field
              label="Password"
              htmlFor="new-user-password"
              description="At least 8 characters."
              required
            >
              <Input
                id="new-user-password"
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
              />
            </Field>
          ) : null}
          <Field label="Role" htmlFor="new-user-role">
            <UserRoleSelect value={role} onChange={setRole} />
          </Field>
          {error ? (
            <Alert variant="destructive">
              <AlertCircle />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={loading}
              disabled={
                !firstName.trim() ||
                !email.trim() ||
                (usesPassword && password.length < 8)
              }
            >
              Create user
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
